<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <h1 class="title">Free days</h1>
      </app-cells>
      <app-cells>
        <app-input
          @input.native="onGroupsSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search by group name"
          name="search"
        />
        <app-date-picker
          @input="onChangeFilter"
          v-model="filter_start"
          placeholder="From"
          class="datepicker--filter"
        />
        <app-date-picker
          @input="onChangeFilter"
          v-model="filter_end"
          placeholder="To"
          class="datepicker--filter"
        />
        <app-select
          @input="onChangeFilter"
          v-model="category_filter"
          :options="category_filters"
          :reduce="item => item.id"
          :filterable="false"
          :searchable="false"
          placeholder="Category"
          class="select--200"
        />
        <app-filters
          v-model="range_type_filter"
          :filters="range_type_filters"
          name="filterRange"
          @change="onChangeFilter"
        />
      </app-cells>
      <div v-for="table_item of free_days_table" :key="table_item.name" class="climbing-block">
        <app-cells>
          <h2 class="title">{{ table_item.name }}</h2>
          <div v-if="table_item.today" class="status-title" :style="{ 'backgroundColor': '#77b017' }">Today</div>
        </app-cells>
        <v-client-table
          :data="table_item.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
        >
          <template #h__name><div class="table-default__left">Name</div></template>
          <template #name="{ row }">
            <div class="table-default__left">
              <router-link :to=" {name: 'free-days-detail', params: { id: row.id } }" class="table-default__link-border">
                {{ row.name }}
              </router-link>
            </div>
          </template>
          <template #clients="{ row }">
            <span
              v-tooltip="{ content: clientTableList(row.clients), placement: 'top-start', offset: 10, html: true }"
            >
              {{ row.clients.length }}
            </span>
          </template>
          <template #tour_start="{ row }">{{ dateFormat(row.tour_start) }} - {{ dateFormat(row.tour_end) }}</template>
          <template #location="{ row }">
            <span
              v-tooltip="{ content: row.location.name, placement: 'top-start', offset: 10 }"
            >
              {{ row.location.short_name }}
            </span>
          </template>
          <template #notes="{ row }">
            <div class="table-default__controls">
              <div
                v-tooltip="{ content: row.notes, placement: 'right', offset: 10 }"
                class="table-default__activity table-default__activity--notes"
                :class="{'table-default__activity--active': row.notes}"
              ></div>
            </div>
          </template>
        </v-client-table>
      </div>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table_length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
  </div>
</template>

<script>
import { getClientsFreeDaysByDates } from '@/http'
import { format, parse } from 'date-fns'
import { debounce } from 'lodash'

export default {
  name: 'FreeDaysList',
  data() {
    return {
      is_loaded: false,
      free_days_table: [],
      table: {
        items: [],
        columns: ['name', 'clients', 'tour_start', 'location', 'notes'],
        options: {
          headings: {
            id: 'Name',
            clients: 'Pax',
            tour_start: 'Dates',
            location: 'Location',
            notes: 'Notes',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      range_type_filter: 'day',
      range_type_filters: [
        { value: 'month', label: 'Month' },
        { value: 'day', label: 'Day' },
      ],
      category_filter: 'upcoming',
      category_filters: [
        {
          id: 'upcoming',
          name: 'Upcoming',
        },
        {
          id: 'archive',
          name: 'Archive',
        }
      ],
      search: '',
      table_length: 0,
      filter_start: null,
      filter_end: null,
      table_preloader: false
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    fetchItems() {
      const params = {
        search: this.search,
        page: this.pagination.page,
        page_size: 40,
        range_type: this.range_type_filter,
        group_by: 'tour_start',
        category: this.category_filter,
      }
      if (!this.search && this.filter_start) params.tour_start__gte = this.filter_start
      if (!this.search && this.filter_end) params.tour_start__lte = this.filter_end
      getClientsFreeDaysByDates(params)
        .then(response => {
          this.free_days_table = response.data.results.length ? response.data.results : [{name: '', items: []}]
          this.pagination.pages = response.data.pages
          this.pagination.count = response.data.count
          this.table_length = 0
          response.data.results.forEach(item => {
            this.table_length += item.items.length
          })
          this.is_loaded = true
          this.table_preloader = false
        })
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.search = ''
      this.fetchItems()
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    clientTableList(clients) {
      let result = ''
      for (const key in clients) {
        result += `${clients[key].first_name} ${clients[key].last_name}<br>`
      }
      return result
    },
    dateFormat(date) {
      return format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM')
    },
    onGroupsSearch() {
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>

<style lang="sass" scoped>
.climbing-block
  margin-bottom: 30px
</style>
